// src/data/ruleCatalog.js - the Amtrak rule RESOLVER and the canonical-label code.
// Its content - AMTRAK_BASE_RULES, AMTRAK_RULE_MODS, AMTRAK_LOCATION_SCOPE - is installation content since ruling 321 slice 3c
// (task list section 730): installations\smart-td\content\amtrakCatalog.js, projected to src\data\amtrakCatalog.js. This file
// re-exports it, so no importer changed. It was GENERATED by scripts\gen-rule-catalog.js until that generator fell behind it
// (the A-6 footer removal, the 2026-08-22 sourced-rule widening, onePerRule of 2026-10-14); it is maintained here, by hand.
//
// The Amtrak rule catalog: base SYSTEM text + a scoped/dated modification layer, and a
// resolver that returns the EFFECTIVE rule for (ruleNumber, location, asOfDate) by applying
// system_wide + matching-territory mods in date order (later instrument wins).

import { AMTRAK_BASE_RULES, AMTRAK_RULE_MODS, AMTRAK_LOCATION_SCOPE } from './amtrakCatalog';
export { AMTRAK_BASE_RULES, AMTRAK_RULE_MODS, AMTRAK_LOCATION_SCOPE };

export function resolveAmtrakRule(ruleNumber, location, asOfDate) {
  const rn = String(ruleNumber);
  const today = asOfDate || new Date().toISOString().slice(0, 10);
  const base = AMTRAK_BASE_RULES[rn] || null;
  const territory = AMTRAK_LOCATION_SCOPE[location] || null;
  const mods = (AMTRAK_RULE_MODS[rn] || [])
    .filter(m => m.date <= today)
    .filter(m => m.scopeType === "system_wide" || (m.scopeType === "territorial" && m.scopeValue === territory))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  let status = base ? "active" : "unknown";
  let title = base ? base.title : "";
  let text = base ? base.text : "";
  let renumberedTo = null;
  const chain = [];
  const qualifiers = [];
  for (const m of mods) {
    chain.push({ instrument: m.instrument, date: m.date, action: m.action, scope: m.scopeType + (m.scopeValue ? (":" + m.scopeValue) : ""), notes: m.notes });
    if (m.action === "deleted") { status = "deleted"; text = ""; }
    else if (m.action === "not_applicable") { status = "not_applicable"; }
    else if (m.action === "revised_in_full" || m.action === "revised") {
      status = "active";
      if (m.revisedText) { text = m.revisedText; }
      else {
        var nt = (m.notes || "").replace(/\s*\.\s*$/, "");
        var scopeLabel = (m.scopeType === "territorial") ? ("for the " + m.scopeValue + " local schedule") : "system-wide";
        var qual = "[QUALIFIER \u2014 Rule " + rn + " is " + m.action.replace(/_/g, " ") + " " + scopeLabel + " by " + m.instrument + " (" + m.date + ")" + (nt ? ": " + nt : "") + (m.action === "revised_in_full" ? ". The text above is the system base; the " + (m.scopeValue || "governing") + " version governs here \u2014 consult " + m.instrument + "." : ".") + "]";
        text = (text ? text + "\n\n" : "") + qual; qualifiers.push(qual);
      }
    } else if (m.action === "revised_renumbered") { status = "active"; renumberedTo = m.notes; }
  }
  return { ruleNumber: rn, title, status, text, territory, renumberedTo, qualifiers, appliedInstruments: chain };
}

// ---- Canonical Amtrak rule labels (single source; SS-XI / PD-4) --------------------------
// One canonical "Rule N — TITLE" label per rule, drawn from AMTRAK_BASE_RULES (the sourced
// Nov 1 2000 printout). Normalizes rules however they enter a claim — AI-matched or
// picker-picked — so the stored basis carries one label, not three. Idempotent on labels that
// are already canonical (e.g. from RuleBasisPicker). Non-catalog / non-numeric rules pass through.
export function canonicalAmtrakRuleLabel(rule) {
  const n = (String(rule).match(/\d+/) || [])[0];
  const r = n && AMTRAK_BASE_RULES[n];
  return r ? ('Rule ' + n + ' — ' + r.title) : null;
}
export function canonicalizeAmtrakRules(rules) {
  if (!Array.isArray(rules)) return rules;
  return rules.map(function (item) {
    const label = canonicalAmtrakRuleLabel(item && item.rule);
    // PD-4 / PD-13 / §XI: Amtrak is a catalogued agreement, so the ONLY authored value kept on the
    // claim is the canonical rule label (which already carries the catalog title). Every AI-authored
    // field from analyzeClaimRules is stripped:
    //   exactLanguage — invented verbatim "quote" of agreement text (fabrication, PD-4).
    //   fullTitle     — AI paraphrase title that DIVERGES from the canonical title in `rule`
    //                   (e.g. "Overtime Violation" vs catalog "OVERTIME, BASIS OF") — a §XI
    //                   two-titles-for-one-rule defect.
    //   relevance     — AI legal characterization; not stored on a catalogued claim.
    // Rule text/title/status are resolved LIVE from the catalog (resolveAmtrakRule) by
    // RuleBasisPicker and ClaimDetail — never persisted AI text. NOTE: this does NOT fix rule
    // SELECTION (the AI proposing a rule whose text doesn't support the theory) — that is Plan 8.
    // WIDENED 2026-08-22 on Bob's ruling, on the same per-rule test used in ruleSourcing.js. A rule
    // that comes back marked `sourced` with a violationId, a source file and a line is the violation
    // map's own verbatim text - VIO-1 has proved it resolves - not an AI paraphrase. Keeping it means
    // an Amtrak claim carries the clause AND where it lives, instead of a bare label. Anything not so
    // marked is stripped exactly as before: the 07 August standard is intact, its inputs changed.
    const sourced = item && item.sourced === true
      && typeof item.violationId === 'string' && item.violationId.length > 0
      && typeof item.sourceFile === 'string' && item.sourceFile.length > 0
      && String(item.sourceLine || '').length > 0;
    const clean = Object.assign({}, item);
    if (!sourced) {
      delete clean.exactLanguage;
      delete clean.fullTitle;
      delete clean.relevance;
    }
    if (label) clean.rule = label;
    return clean;
  }).filter(onePerRule());
}

// Bob 2026-10-14 ("the same rule twice?"): two analysis hits under one rule canonicalize to the same label, and the
// claim carried Rule 30 twice. One entry per rule label; the first kept.
export function onePerRule() {
  const seen = new Set();
  return function (item) {
    const k = String((item && item.rule) || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (!k) return true;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  };
}
